import { Injectable } from '@angular/core';

import { TranslationService } from '@alfresco/adf-core';

import {
  VALIDATION_TYPES,
  ValidationGroup,
  ValidationResults,
} from '../model/validation-service.model';

const ERROR_STATUSES = new Set([
  'ERROR',
  'INVALID',
  'FAILED',
  'PROCESSING_FAILED',
]);
const NO_ERRORS_FOUND_KEY = 'APP.ACTIONS.NO_ERRORS_FOUND';
const VALIDATION_LOGS_TITLE_KEY = 'APP.ACTIONS.VALIDATION_LOGS_TITLE';

@Injectable({
  providedIn: 'root',
})
export class ValidationLogsDownloadService {
  constructor(private readonly translationService: TranslationService) {}

  /**
   * Returns only ERROR validation entries.
   */
  public getErrorEntries(
    validationResults: ValidationResults[]
  ): ValidationResults[] {
    return (validationResults || []).filter((entry) =>
      this.isErrorEntry(entry)
    );
  }

  /**
   * Checks whether a validation entry represents an error (ERROR, INVALID, or FAILED).
   */
  public isErrorEntry(entry: ValidationResults): boolean {
    const normalizedStatus = (entry?.status || '')
      .toString()
      .trim()
      .toUpperCase();
    return ERROR_STATUSES.has(normalizedStatus);
  }

  /**
   * Generates and downloads validation logs as HTML file
   */
  public downloadValidationLogs(
    validationResults: ValidationResults[],
    documentName: string
  ): void {
    const htmlContent = this.generateHTMLContent(validationResults);
    const filename = this.generateFilename(documentName);
    this.downloadFile(htmlContent, filename);
  }

  /**
   * Generates filename for validation logs
   */
  private generateFilename(documentName: string): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const nameWithoutExt = documentName.replace(/\.[^/.]+$/, '');
    const sanitizedDocName = nameWithoutExt
      .replace(/[^a-z0-9]/gi, '-')
      .toLowerCase();
    return `validation-logs-${sanitizedDocName}-${timestamp}.html`;
  }

  /**
   * Generates HTML content for validation logs with two-panel interactive layout.
   */
  private generateHTMLContent(validationResults: ValidationResults[]): string {
    const groups = this.groupValidationResults(validationResults);
    const radioButtons = this.generateRadioButtons(groups);
    const sidebarLabels = this.generateSidebarLabels(groups);
    const detailPanels = this.generateDetailPanels(groups);
    const title = this.translationService.instant(VALIDATION_LOGS_TITLE_KEY);

    return `<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${title}</title>
    <style>${this.generateValidationCSS(groups)}</style>
</head>
<body>
    <div class="container">
        ${radioButtons}
        <div class="content">
            <div class="header-left">${title}</div>
            <div class="sidebar">${sidebarLabels}</div>
            <div class="details-panel">${detailPanels}</div>
        </div>
    </div>
</body>
</html>`;
  }

  /**
   * Groups validation results by type dynamically based on entry.type field.
   * Assumes input is already filtered to error-only entries.
   */
  public groupValidationResults(
    validationResults: ValidationResults[]
  ): ValidationGroup[] {
    const typeMap = new Map<string, ValidationResults[]>();
    VALIDATION_TYPES.forEach((type) => typeMap.set(type, []));
    validationResults.forEach((entry) => {
      if (!entry.type) {
        return;
      }
      const type = entry.type.toUpperCase();
      if (!typeMap.has(type)) {
        typeMap.set(type, []);
      }
      const typeEntries = typeMap.get(type);
      if (typeEntries) {
        typeEntries.push(entry);
      }
    });

    return Array.from(typeMap.entries()).map(([type, entries]) => {
      const errorCount = entries.length;
      return {
        name: type,
        status: errorCount > 0 ? 'error' : 'success',
        errorCount,
        results: entries,
      };
    });
  }

  /**
   * Generates CSS for two-panel layout with grid structure and radio button controls
   */
  private generateValidationCSS(groups: ValidationGroup[]): string {
    const tabSelectors = groups
      .map(
        (_, index) =>
          `#tab-${index}:checked ~ .content .details-panel [data-index="${index}"]`
      )
      .join(',\n      ');

    return `
            :root { color-scheme: light; }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; background: #f5f5f5; color: #212121; font-size: 13px; line-height: 1.5; display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; }
            .container { width: 100%; max-width: 1200px; height: 85vh; display: flex; flex-direction: column; background: #ffffff; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
            .content { display: grid; grid-template-columns: 250px 1fr; grid-template-rows: auto 1fr; flex: 1; overflow: hidden; }
            .header-left { grid-column: 1 / -1; grid-row: 1; padding: 16px 20px; border-bottom: 1px solid #e0e0e0; background: #fafafa; font-size: 16px; font-weight: 600; text-align: left; color: #212121; }
            .sidebar { grid-column: 1; grid-row: 2; background: #fafafa; overflow-y: auto; border-right: 1px solid #e0e0e0; }
            .format-item { padding: 14px 16px; border-bottom: 1px solid #e0e0e0; cursor: pointer; transition: background 0.15s; display: flex; align-items: center; gap: 8px; font-size: 13px; color: #212121; }
            .format-item:hover { background: #f0f0f0; }
            .format-name { flex: 1; font-weight: 500; color: #212121; }
            .format-status { display: flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; border-radius: 50%; font-size: 11px; font-weight: 700; color: #ffffff; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
            .format-status.success { background: #4caf50; }
            .format-status.error { background: #d32f2f; }
            .details-panel { grid-column: 2; grid-row: 2; background: #ffffff; overflow-y: auto; display: flex; flex-direction: column; }
            .log-list { flex: 1; overflow-y: auto; display: none; flex-direction: column; }
            .log-item { padding: 14px 20px; border-bottom: 1px solid #f0f0f0; display: grid; grid-template-columns: 110px 1fr; gap: 20px; font-size: 12px; transition: background 0.1s; color: #212121; }
            .log-item:nth-child(even) { background: #fafafa; }
            .log-item:hover { background: #f5f5f5; }
            .log-timestamp { color: #616161; white-space: nowrap; font-family: 'Consolas', 'Monaco', monospace; font-size: 0.8125rem; }
            .log-message { color: #212121; line-height: 1.6; word-wrap: break-word; font-size: 0.875rem; }
            .empty-state { text-align: center; padding: 60px 20px; color: #212121; font-size: 1.125rem; font-weight: 600; }
            .tab-input { display: none; }
            ${tabSelectors} { display: flex; }
            @media print { body { padding: 0; background: #ffffff; } .container { box-shadow: none; height: auto; } }
        `;
  }

  /**
   * Generates hidden radio buttons for tab control
   */
  private generateRadioButtons(groups: ValidationGroup[]): string {
    return groups
      .map(
        (_, index) =>
          `<input type="radio" name="tab" id="tab-${index}" class="tab-input" ${
            index === 0 ? 'checked' : ''
          }>`
      )
      .join('');
  }

  /**
   * Generates sidebar labels with validation types and error counts
   */
  private generateSidebarLabels(groups: ValidationGroup[]): string {
    return groups
      .map(
        (group, index) => `
            <label for="tab-${index}" class="format-item">
                <span class="format-name">${this.escapeHtml(group.name)}</span>
                <span class="format-status ${group.status}">${
          group.errorCount || 0
        }</span>
            </label>
        `
      )
      .join('');
  }

  /**
   * Generates detail panels for each validation type group
   */
  private generateDetailPanels(groups: ValidationGroup[]): string {
    return groups
      .map((group, index) => this.generateDetailPanel(group, index))
      .join('');
  }

  /**
   * Generates a single detail panel with log items
   */
  private generateDetailPanel(group: ValidationGroup, index: number): string {
    const logItems = this.generateLogItems(group);
    const content =
      logItems ||
      `<div class="empty-state">${this.translationService.instant(
        NO_ERRORS_FOUND_KEY
      )}</div>`;
    return `
                <div data-index="${index}" class="log-list">
                    ${content}
                </div>
            `;
  }

  /**
   * Generates individual log items with timestamps and messages
   */
  private generateLogItems(group: ValidationGroup): string {
    const now = new Date();
    return group.results
      .map((item: ValidationResults, itemIndex: number) => {
        const timestamp = new Date(
          now.getTime() - (group.results.length - itemIndex) * 1000
        );
        const message = item.text || item.subject || 'No message';
        return `
                    <div class="log-item">
                        <div class="log-timestamp">${this.formatTimestamp(
                          timestamp
                        )}</div>
                        <div class="log-message">${this.escapeHtml(
                          message
                        )}</div>
                    </div>
                `;
      })
      .join('');
  }

  /**
   * Formats a date into "DD Mon HH:MM" format.
   */
  public formatTimestamp(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en', { month: 'short' });
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${hours}:${minutes}`;
  }

  /**
   * Escapes HTML special characters.
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Triggers file download in browser.
   */
  private downloadFile(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  }
}
